// Zebra Simple Dashboards — frontend API surface.
//
// After the hardcoded-dashboards migration the CRUD surface (create/update/
// delete/list dashboards and charts) is gone — dashboards are code files under
// modules/zebra-simple-dashboards/hardcoded/ and layout comes from those
// files, not the DB. The only routes left are the READ-ONLY data endpoints
// the card renderers call to build their contents (aggregate / segment-counts
// / segment-matrix / timeseries / issues / fields).

import { client } from '@platform/http/api-client';

const zsdApi = {
  // group-by-a-field → { groupBy, groupBySecondary, metric, buckets, warnings }
  async zsdAggregate(body) {
    return (await client.post('/api/zsd/aggregate', body)).data;
  },
  // custom-JQL segments → { segments: [{ label, jql, count, error? }] }
  async zsdSegmentCounts(body) {
    return (await client.post('/api/zsd/segment-counts', body)).data;
  },
  // custom-JQL × custom-JQL stacking → { primaries, secondaries, rows }
  async zsdSegmentMatrix(body) {
    return (await client.post('/api/zsd/segment-matrix', body)).data;
  },
  // per-primary time-in-status breakdown → { primary: [{ label, jql, statuses: [{ name, seconds }], error? }] }
  async zsdTimeInStatus(body) {
    return (await client.post('/api/zsd/time-in-status', body)).data;
  },
  // Jira's issue-history gadget: a numeric field per DAY per status, over the
  // items that were in that status on the day → { periods, series }
  async zsdStatusHistory(body) {
    return (await client.post('/api/zsd/status-history', body)).data;
  },
  // date-bucket "over time" → { grouping, dateField, periods, series, total }
  async zsdTimeseries(body) {
    return (await client.post('/api/zsd/timeseries', body)).data;
  },
  // issue-list rows for the folder gadget → { columns, rows, total, offset, limit }
  async zsdIssues(body) {
    return (await client.post('/api/zsd/issues', body)).data;
  },
  // Boards as DATA — the product's registry read (rows in the ZSD store;
  // the archive kept boards in code and had no list call).
  async zsdListBoards() {
    return (await client.get('/api/zsd/boards')).data;
  },
  async zsdGetBoard(slug) {
    return (await client.get(`/api/zsd/boards/${encodeURIComponent(slug)}`)).data;
  },
  // field catalogue — kept for the (rare) card renderer that needs field types.
  async zsdGetFields() {
    return (await client.get('/api/zsd/fields')).data;
  },
  // Persisted layout for a hardcoded dashboard. GET returns { layout: null }
  // when nothing has been saved yet — the caller falls back to hardcoded
  // defaults. PUT is admin-only server-side (403 otherwise).
  async zsdGetLayout(slug) {
    return (await client.get(`/api/zsd/layout/${encodeURIComponent(slug)}`)).data;
  },
  async zsdPutLayout(slug, layout) {
    return (await client.put(`/api/zsd/layout/${encodeURIComponent(slug)}`, { layout })).data;
  },
  // Per-widget config override — admin-only server-side. Used by cards that
  // need runtime configuration (e.g. Structure/Gantt card's folderId).
  async zsdPutWidgetConfig(slug, widgetId, config) {
    return (await client.put(
      `/api/zsd/widget-config/${encodeURIComponent(slug)}/${encodeURIComponent(widgetId)}`,
      { config },
    )).data;
  },
};

export default zsdApi;
